import { useState, useRef, useCallback } from "react";
import api from "@/lib/api";

// Records mic audio, exposes real-time audio levels, uploads to Whisper, returns transcript.
export function useVoiceRecorder() {
  const [state, setState] = useState("idle"); // idle | recording | paused | transcribing
  const [seconds, setSeconds] = useState(0);
  const [quiet, setQuiet] = useState(false);
  const quietRef = useRef(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const mimeRef = useRef("audio/webm");

  // Every second: advance timer and flag the mic as too quiet after 4 silent seconds.
  const tick = () => {
    timerRef.current = setInterval(() => {
      setSeconds((s) => s + 1);
      const a = analyserRef.current;
      if (!a) return;
      const arr = new Uint8Array(a.frequencyBinCount);
      a.getByteFrequencyData(arr);
      const avg = arr.reduce((x, y) => x + y, 0) / arr.length;
      quietRef.current = avg < 6 ? quietRef.current + 1 : 0;
      setQuiet(quietRef.current >= 4);
    }, 1000);
  };
  const stopTick = () => { if (timerRef.current) clearInterval(timerRef.current); timerRef.current = null; };

  const teardownAudio = () => {
    try { audioCtxRef.current?.close(); } catch (e) { console.warn("Audio context close failed", e); }
    audioCtxRef.current = null;
    analyserRef.current = null;
  };

  // Returns live frequency data (Uint8Array) or null when not recording.
  const getFrequency = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return null;
    const arr = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(arr);
    return arr;
  }, []);

  const start = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 } });
    quietRef.current = 0;
    setQuiet(false);
    streamRef.current = stream;

    // Real-time analyser for waveform visualisation.
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (AudioCtx) {
      const ctx = new AudioCtx();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 64;
      analyser.smoothingTimeConstant = 0.7;
      source.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
    }

    const mr = new MediaRecorder(stream, MediaRecorder.isTypeSupported?.("audio/webm;codecs=opus") ? { mimeType: "audio/webm;codecs=opus", audioBitsPerSecond: 128000 } : undefined);
    mimeRef.current = mr.mimeType || "audio/webm";
    chunksRef.current = [];
    mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mr.start(1000);
    mediaRef.current = mr;
    setSeconds(0);
    setState("recording");
    tick();
  }, []);

  const pause = useCallback(() => { mediaRef.current?.pause(); stopTick(); setState("paused"); }, []);
  const resume = useCallback(() => { mediaRef.current?.resume(); tick(); setState("recording"); }, []);

  const abort = useCallback(() => {
    const mr = mediaRef.current;
    stopTick();
    if (mr) { mr.onstop = () => {}; try { mr.stop(); } catch (e) { console.warn("MediaRecorder stop failed", e); } }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    teardownAudio();
    chunksRef.current = [];
    setState("idle");
  }, []);

  const stop = useCallback((consultationId) => {
    return new Promise((resolve) => {
      const mr = mediaRef.current;
      if (!mr) return resolve(null);
      stopTick();
      mr.onstop = async () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        teardownAudio();
        const mime = mimeRef.current || "audio/webm";
        const ext = /mp4/.test(mime) ? "mp4" : /ogg/.test(mime) ? "ogg" : /wav/.test(mime) ? "wav" : "webm";
        const blob = new Blob(chunksRef.current, { type: mime });
        if (blob.size < 2000) { setState("idle"); return resolve({ error: "No audio was captured. Check that your microphone is selected and not muted, then try again." }); }
        setState("transcribing");
        try {
          const fd = new FormData();
          fd.append("file", blob, `consult.${ext}`);
          if (typeof consultationId === "string") fd.append("consultation_id", consultationId);
          const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setState("idle");
          resolve(data.text || "");
        } catch (e) {
          setState("idle");
          resolve({ error: e.response?.data?.detail || "Transcription failed" });
        }
      };
      mr.stop();
    });
  }, []);

  return { state, seconds, quiet, start, pause, resume, stop, abort, getFrequency };
}
